// ============================================================
// PreChiffrage — les volumes, le brut, la matière, avec le CALCUL.
// ------------------------------------------------------------
// Tout ce bloc était calculé et affiché nulle part. Volume brut, volume
// de copeaux avec son pourcentage, volume de la pièce finie, cotes du
// brut, masse, coût matière : le programme les connaissait, l'écran non.
//
// ── Pourquoi le détail du calcul est affiché ──
//
//   V = π × (Ø/2)² × L : π × (79/2)² × 11 = 53,92 cm³
//
// se vérifie de tête en trois secondes. « 53,92 cm³ » se croit ou ne se
// croit pas. C'est ce qui rend un chiffre crédible — et c'est ce que
// faisait AGC.
//
// ── Les QUATRE boutons de forme, et c'est LE SEUL contrôle ──
//
// §3.7 : la forme du brut se choisissait à DEUX endroits, avec DEUX
// vocabulaires — « Prismatique » dans une liste déroulante,
// « Parallélépipède » sur un bouton. Il n'en reste qu'un, ici, et un
// seul mot par forme (`BRUT_LABELS`).
//
// Ce n'était pas qu'une gêne de lecture : ces boutons écrivaient encore
// `box | cylinder | cylinder_box` alors que le schéma est passé aux
// quatre nouveaux noms. **Cliquer une forme rendait le devis
// inenregistrable en 400**, exactement comme le champ « Marge » du lot
// D. Un contrôle orphelin n'est pas inerte.
//
// La forme reste DÉDUITE par une règle, avec sa raison affichée, et
// marquée « à confirmer » quand c'est un défaut et non une décision
// (§3.4). Les boutons permettent de la trancher autrement : la règle est
// juste, elle n'est pas souveraine.
//
// ── Le basculement est INSTANTANÉ — §3.5 ──
//
// Les quatre variantes sont précalculées à la lecture et stockées sur la
// pièce (`brut_variants`). Changer de forme LIT une variante déjà
// connue : volume, copeaux, masse et prix suivent sans aucun
// aller-retour serveur.

import { useMemo } from 'react';
import Badge from '../../ui/Badge.jsx';
import { Select } from '../../ui/TextField.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { formatEuroOuTiret, formatMesure } from '../../lib/format.js';
import { BRUT_SHAPES, BRUT_LABELS, BRUT_EFFETS, FORMES_RONDES } from '@shared/brut.js';
import { brutAffiche } from './brutAffiche.js';
import DetailCalcul, { LigneOffre } from './DetailCalcul.jsx';
import BrutFourni, { useModeBrutFourni } from './BrutFourni.jsx';
// LA MÊME fonction que le serveur, sur la MÊME phrase. Deux décodages
// donneraient deux bruts sur le même écran — c'est exactement ce que
// `shared/` existe pour empêcher.
import { brutDeLaConsigne } from '@shared/consigneBrut.js';

// Un seul vocabulaire, importé — jamais recopié. C'est la recopie qui a
// produit les deux vocabulaires et le menu à options vides.
const FORMES = BRUT_SHAPES.map((valeur) => ({ valeur, label: BRUT_LABELS[valeur] }));

// Aucun formatage local : tout passe par `lib/format.js`. Un formateur
// posé dans un écran court-circuite le tiret, et c'est comme ça qu'un

export function PreChiffrage({ piece, result, disabled, onChange, partie = 'tout' }) {
  const mat = result?.detail?.material ?? {};

  /**
   * Les volumes AFFICHÉS sont ceux qui sont CHIFFRÉS.
   *
   * Le calcul vit dans `brutAffiche.js` — pur, donc testable sans
   * navigateur, et lisible d'un seul tenant. Son en-tête raconte le
   * défaut qui l'a motivé : l'écran lisait la variante précalculée
   * pendant que le prix lisait les colonnes de la pièce, et les deux
   * divergeaient dès qu'une consigne imposait un brut.
   */
  const geo = useMemo(
    () => brutAffiche(piece, mat, piece.geometrie ?? null),
    [piece, mat],
  );

  /** Le brut que le deviseur a ANNONCÉ dans sa consigne, s'il l'a fait. */
  const brutConsigne = useMemo(
    () => brutDeLaConsigne(piece.consigne_gamme),
    [piece.consigne_gamme],
  );

  /** Les cotes AFFICHÉES suivent la forme — c'est la cause racine du
   *  bug d'origine : on demandait longueur/largeur/épaisseur sur un
   *  disque, trois cotes qui n'existent pas dessus. */
  const champsCotes = useMemo(() => {
    if (FORMES_RONDES.includes(piece.raw_shape)) {
      return [
        { clef: 'raw_diameter', label: 'Ø de la barre', hint: 'Ce qu’on commande.' },
        { clef: 'raw_length', label: 'Longueur débitée', hint: 'Ce qu’on scie.' },
      ];
    }
    // ── LE VOCABULAIRE D'ACHAT — 21 août 2026 ──
    //
    // Charles : « en barreau prismatique, ce n'est pas clair : qu'est-ce
    // que la longueur, la largeur et l'épaisseur ? »
    //
    // Rien ne le disait, et rien ne le garantissait : les trois cotes
    // n'étaient pas ordonnées, donc la même pièce décrite dans un autre
    // ordre donnait un autre brut — et le programme allait chercher une
    // « épaisseur » de 110 mm là où la section fait 20.
    //
    // `cotesBrut` les ordonne désormais selon la convention de l'atelier,
    // et les libellés la nomment : on COMMANDE un plat de section
    // largeur × épaisseur, et on en DÉBITE une longueur.
    return [
      { clef: 'raw_l', label: 'Longueur débitée', hint: 'La plus grande cote — ce qu’on scie.' },
      { clef: 'raw_w', label: 'Largeur', hint: 'Section commandée.' },
      { clef: 'raw_h', label: 'Épaisseur', hint: 'La plus petite cote — section commandée.' },
    ];
  }, [piece.raw_shape]);

  /** Changer de forme vide les cotes de l'autre famille : les laisser
   *  produirait un volume calculé sur des cotes qui ne s'affichent plus. */
  function choisirForme(valeur) {
    // ── §3.5 — la variante est DÉJÀ là ──
    //
    // On lit `brut_variants[valeur]`, précalculée à la lecture du plan.
    // Aucun appel serveur : les cotes, le volume et les copeaux sont
    // connus, et le prix suit dans la foulée par `computePiece`.
    const variante = piece.brut_variants?.[valeur] ?? null;
    const c = variante?.cotes ?? null;

    // Les cotes de l'AUTRE famille sont vidées : les laisser produirait
    // un volume calculé sur des cotes qui ne s'affichent plus.
    onChange({
      raw_shape: valeur,
      raw_diameter: c?.diametre_mm ?? null,
      raw_length: FORMES_RONDES.includes(valeur) ? (c?.longueur_mm ?? null) : null,
      raw_l: FORMES_RONDES.includes(valeur) ? null : (c?.longueur_mm ?? null),
      raw_w: FORMES_RONDES.includes(valeur) ? null : (c?.largeur_mm ?? null),
      raw_h: FORMES_RONDES.includes(valeur) ? null : (c?.epaisseur_mm ?? null),
    });
  }

  // ── §C — proposé et retenu ne partagent jamais la même colonne ──
  //
  // `partie` découpe ce bloc en deux, et le découpage suit exactement
  // cette frontière :
  //
  //   'analyse' → ce que le PROGRAMME a déduit du plan : volumes, ratio
  //               de copeaux, usinages relevés, tolérances. Lecture
  //               seule, colonne de GAUCHE.
  //   'brut'    → ce que le DEVISEUR retient : la forme et les cotes.
  //               Colonne de DROITE, avec la matière et le prix, parce
  //               que la forme du brut est une décision d'achat.
  //
  // Empilés l'un sous l'autre, comme ils l'étaient, on ne distingue plus
  // ce que le programme propose de ce que l'homme a tranché.
  const montrerBrut = partie !== 'analyse';
  const brutFourni = useModeBrutFourni(piece); // matière fournie, brut incomplet
  const montrerAnalyse = partie !== 'brut';

  const selectForme = (
    <Select
      label="Forme du brut" hideLabel
      value={piece.raw_shape ?? ''}
      disabled={disabled}
      options={FORMES.map((f) => ({ value: f.valeur, label: f.label }))}
      hint={BRUT_EFFETS[piece.raw_shape] ?? undefined}
      onChange={(e) => choisirForme(e.target.value)}
    />
  );

  return (
    <div className="prechiffrage">
      <div className="row row--between">
        <p className="caps">
          {montrerBrut && montrerAnalyse ? 'Brut et volumes' : montrerBrut ? 'Forme du brut' : 'Géométrie déduite du plan'}
          {piece.ref ? ` — ${piece.ref}` : ''}
        </p>
        {montrerBrut && geo?.brut_shape ? (
          <Badge tone={geo.brut_par_defaut ? 'warn' : 'neutral'}>
            <SourceDot source="ai" title="Déduit par le programme" />{' '}
            {/* §3.4 — un défaut et une décision ne se lisent pas pareil.
                Sans cette distinction, « barreau » sur une pièce
                prismatique et « barreau » sur une pièce non classée
                s'afficheraient à l'identique. */}
            {geo.brut_par_defaut ? 'forme par défaut, à confirmer' : 'forme déduite'}
          </Badge>
        ) : null}
      </div>

      {montrerBrut && geo?.brut_raison ? (
        <p className="field__hint">{geo.brut_raison}</p>
      ) : null}

      {/* UNE LISTE, PAS QUATRE BOUTONS : elle affiche la réponse retenue et
          garde les alternatives à un clic, dans la moitié de la place. */}
      {montrerBrut && brutFourni ? (
        <BrutFourni piece={piece} disabled={disabled} onChange={onChange} forme={selectForme} />
      ) : null}
      {montrerBrut && !brutFourni ? selectForme : null}

      {/* Ce que la forme retenue CHANGE. Indispensable sur « barre ronde
          reprise en prismatique », qui rend exactement les mêmes cotes que
          « barre ronde » : sans cette ligne, Charles clique et aucun
          chiffre de ce bloc ne bouge, sans qu'il puisse savoir pourquoi.
          Ce qui change est la gamme, pas le brut. */}
      {/* « Débité dans une plaque ou un plat » vit en INFOBULLE de la liste. */}

      {/* ── LE BRUT ANNONCÉ DANS LA CONSIGNE — 21 août 2026 ──
          Charles : « quand je mets *je pars d'un brut de 510x110x100*
          dans "comment usineriez-vous cette pièce", il faudrait que ça
          modifie aussi le calcul du brut. »

          C'est fait, côté serveur et de façon déterministe. Ce bandeau
          existe pour que ça ne se fasse pas EN SILENCE : il cite le
          fragment exact qui a été lu, donc le deviseur voit d'où vient
          le brut affiché et peut corriger sa phrase — qui est la seule
          porte pour cette valeur. */}
      {montrerBrut && brutConsigne.trouve ? (
        <p className="field__hint">
          <strong>Brut imposé par votre consigne</strong> : «&nbsp;
          {brutConsigne.fragment}&nbsp;». Les surépaisseurs ne
          s’appliquent pas — c’est le brut dont vous partez.
          {brutConsigne.partiel
            ? ' La longueur vient de la pièce : la consigne ne donnait que la section.'
            : ''}
          {' '}Modifiez la phrase pour changer ces cotes.
        </p>
      ) : null}

      {montrerBrut && !brutFourni ? (
      <div className="cotes-brut">
        {champsCotes.map((c) => (
          <NumberField
            key={c.clef}
            label={c.label}
            unit="mm"
            min={0}
            // Le libellé seul ne suffisait pas : « longueur » et
            // « largeur » ne disent pas laquelle on commande et laquelle
            // on débite. L'indice le dit, sous le champ, à l'endroit où
            // on hésite.
            hint={c.hint}
            source={piece.raw_shape && geo ? 'computed' : undefined}
            value={piece[c.clef]}
            disabled={disabled}
            onChange={(v) => onChange({ [c.clef]: v })}
          />
        ))}
      </div>
      ) : null}

      {montrerBrut ? (
        <DetailCalcul
          geo={geo}
          mat={mat}
          qty={Math.max(1, Number(piece.qty) || 1)}
          ronde={FORMES_RONDES.includes(piece.raw_shape)}
          detail={result?.detail ?? null}
        />
      ) : null}

      {/* Comment on est arrivé à ces cotes — surépaisseurs et montée à la
          taille commerciale. Replié : c'est une vérification, pas une
          lecture courante. */}
      {montrerBrut && geo?.cotes_brut_detail ? (
        <details className="repli">
          <summary>D’où viennent ces cotes</summary>
          <p className="prechiffrage__calcul">{geo.cotes_brut_detail}</p>
        </details>
      ) : null}

      {montrerAnalyse && geo ? (
        <>
          <div className="prechiffrage__volumes">
            <div>
              <p className="caps">Volume brut</p>
              <p className="num prechiffrage__valeur">
                {formatMesure(geo?.volume_brut_cm3, 'cm³')}
              </p>
            </div>
            <div>
              <p className="caps">Volume pièce finie</p>
              <p className="num prechiffrage__valeur">
                {formatMesure(geo?.volume_piece_cm3, 'cm³')}
              </p>
            </div>
            <div>
              <p className="caps">Copeaux</p>
              <p className="num prechiffrage__valeur">
                {formatMesure(geo?.volume_copeaux_cm3, 'cm³')}{' '}
                {geo.ratio_enlevement != null ? (
                  <Badge tone={geo.ratio_enlevement > 0.7 ? 'warn' : 'neutral'}>
                    {Math.round(geo.ratio_enlevement * 100)} %
                  </Badge>
                ) : null}
              </p>
            </div>
          </div>
          {geo.volume_detail ? <p className="prechiffrage__calcul">{geo.volume_detail}</p> : null}
        </>
      ) : montrerAnalyse ? (
        // Le code SAIT lequel des deux manque : il le DIT. Un « ou »
        // dans un message d'erreur veut dire qu'on n'a pas regardé.
        // Et une taille commerciale absente n'est PAS un encombrement
        // non relevé : c'est la seconde phrase, pas la première.
        <p className="field__hint">
          {/* `.message`, et pas l'objet : `manque_cote` est
              `{ cotes, forme, message }`. Le rendre tel quel faisait
              lever React (« Objects are not valid as a React child »),
              donc écran blanc sur la pièce. */}
          {piece.geometrie_probleme
            ?? piece.brut_variants?.[piece.raw_shape]?.manque_cote?.message
            ?? 'Volumes non calculables : l’encombrement de la pièce n’a pas été relevé sur le plan.'}
        </p>
      ) : null}

      {montrerAnalyse ? (
      <div className="prechiffrage__volumes">
        <div>
          {/* La masse de la pièce FINIE — celle que le client reçoit.
              L'écran affichait ici la masse du BRUT, identique à la masse
              achetée, sur une pièce d'où l'on sort un tiers de copeaux. */}
          <p className="caps">Masse de la pièce finie</p>
          <p className="num prechiffrage__valeur">
            {formatMesure(mat.mass_finished_kg, 'kg')}
          </p>
        </div>
        <div>
          <p className="caps">Masse achetée</p>
          <p className="num prechiffrage__valeur">{formatMesure(mat.mass_total_kg, 'kg')}</p>
        </div>
        <div>
          {/* ══════════════════════════════════════════════════════
              DEUX NOMBRES SOUS UN SEUL MOT — corrigé le 8 sept. 2026.
              ──────────────────────────────────────────────────────
              Cette case disait « Coût matière : 213,56 € » pendant que
              la synthèse disait « Matière : 234,92 € ». Les deux sont
              justes — c'est l'ACHAT et la REVENTE, à 10 % près — mais
              elles portaient le même nom, et le §6 réserve « coût
              matière » au second (coefficient compris).

              On nomme donc l'achat pour ce qu'il est, et on affiche ce
              qui part sur l'offre juste en dessous : deux nombres qui
              s'expliquent l'un l'autre valent mieux que deux nombres
              qui se contredisent. */}
          <p className="caps">Achat matière</p>
          <p className="num prechiffrage__valeur">{formatEuroOuTiret(mat.cost_total)}</p>
          <LigneOffre mat={mat} detail={result?.detail} />
        </div>
      </div>
      ) : null}

      {/* Une masse « — » se lit « je ne sais pas » ; elle doit dire
          POURQUOI, sinon le deviseur cherche. */}
      {montrerAnalyse && mat.masse_inconnue ? (
        <p className="field__hint">
          Masses et achat matière non calculables — {mat.masse_inconnue_raison}.
        </p>
      ) : null}

      {montrerAnalyse && piece.entites?.length ? (
        <div>
          <p className="caps">Usinages relevés{piece.ref ? ` — ${piece.ref}` : ''}</p>
          <ul className="entites">
            {piece.entites.map((e, i) => (
              <li key={e.id ?? i}>
                <strong>
                  {e.nombre ?? 1} × {e.type}
                </strong>
                {e.diametre_mm ? ` Ø${e.diametre_mm}` : ''}
                {e.diametre2_mm ? ` / Ø${e.diametre2_mm}` : ''}
                {e.profondeur_mm ? ` prof. ${e.profondeur_mm}` : ''}
                {e.tolerance ? ` — ${e.tolerance}` : ''}
                <Badge tone={e.groupe_face === 'inconnue' ? 'warn' : 'neutral'}>
                  côté {e.groupe_face ?? 'inconnue'}
                </Badge>
                {e.note ? <span className="field__hint"> {e.note}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {montrerAnalyse && piece.tolerances_critiques?.length ? (
        <div>
          <p className="caps">Cotes et tolérances critiques</p>
          <ul className="entites">
            {piece.tolerances_critiques.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default PreChiffrage;
